// V161.11: User Profile Coordinator - Comprehensive Deduplication
// AUGMENT: Eliminates 5+ duplicate user profile queries into 1-2 optimized requests
// Follows V160 Map-based deduplication pattern for zero duplicate requests

import { supabase } from '@/integrations/supabase/client';
import { rpcPeerUsersForViewer } from '@/lib/d001PeerUsersRpc';

// =============================================================================
// GLOBAL STATE (Module-level singletons - V160 pattern)
// =============================================================================

// Active request tracking: Map<cacheKey, Promise>
const pendingProfileRequests = new Map<string, Promise<any>>();

// Profile cache with TTL (5 minutes like avatar cache)
const profileCache = new Map<string, { data: any; timestamp: number }>();
const PROFILE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Request count tracking for debugging
const profileRequestCounts = new Map<string, number>();

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export interface UserProfileData {
  id: string;
  name: string;
  email?: string;
  photo_urls?: string[];
  bio?: string;
  dob?: string; // Legacy field name for compatibility
  date_of_birth?: string; // V167.FIX: Actual database column name
  gender?: string;
  trust_score?: number;
  is_verified?: boolean;
  dating_preferences?: any;
  account_status?: string;
  created_at?: string;
  updated_at?: string;
  profiles?: {
    id?: string;
    relationship_status?: string;
    dating_goal?: string;
  };
}

export interface ProfileRequestOptions {
  includeProfiles?: boolean;
  fields?: string[];
  useCache?: boolean;
}

// =============================================================================
// CORE PROFILE FUNCTIONS
// =============================================================================

/**
 * V161.11: Get comprehensive user profile with deduplication
 * Consolidates all user profile queries into single optimized request
 * 
 * @param userId - User ID to fetch profile for
 * @param options - Request options
 * @returns Promise<UserProfileData | null>
 */
export async function getUserProfile(
  userId: string, 
  options: ProfileRequestOptions = {}
): Promise<UserProfileData | null> {
  if (!userId) {
    console.warn('🚨 V161.11: getUserProfile called with empty userId');
    return null;
  }

  const {
    includeProfiles = true,
    fields = [],
    useCache = true
  } = options;

  // Generate cache key
  const cacheKey = `profile:${userId}:${includeProfiles}:${fields.join(',')}`;

  // Check cache first
  if (useCache) {
    const cached = profileCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < PROFILE_CACHE_TTL) {
      console.log(`✅ V161.11: Using cached profile for ${userId}`);
      return cached.data;
    }
  }

  // Check for pending request
  if (pendingProfileRequests.has(cacheKey)) {
    console.log(`🔄 V161.11: Waiting for pending profile request for ${userId}`);
    return pendingProfileRequests.get(cacheKey)!;
  }

  // Create new request
  const profilePromise = fetchUserProfileData(userId, options);
  pendingProfileRequests.set(cacheKey, profilePromise);

  // Track request count
  const count = profileRequestCounts.get(userId) || 0;
  profileRequestCounts.set(userId, count + 1);

  try {
    const result = await profilePromise;
    
    // Cache the result
    if (useCache && result) {
      profileCache.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      });
    }

    console.log(`🚀 V161.11: Loaded profile for ${userId} (request #${count + 1})`);
    return result;
  } catch (error) {
    console.error(`❌ V161.11: Failed to load profile for ${userId}:`, error);
    return null;
  } finally {
    pendingProfileRequests.delete(cacheKey);
  }
}

/**
 * V161.11: Batch load multiple user profiles
 * Optimizes multiple individual requests into single batch query
 */
export async function batchGetUserProfiles(
  userIds: string[],
  options: ProfileRequestOptions = {}
): Promise<Map<string, UserProfileData>> {
  const results = new Map<string, UserProfileData>();
  
  if (!userIds.length) return results;

  const uniqueUserIds = [...new Set(userIds)];
  console.log(`🚀 V161.11: Batch loading ${uniqueUserIds.length} profiles`);

  try {
    const selectFields = buildSelectFields(options);
    const { data: authData } = await supabase.auth.getUser();
    const selfId = authData?.user?.id;
    const ownIds = uniqueUserIds.filter(id => id === selfId);
    const peerIds = uniqueUserIds.filter(id => id !== selfId);

    const merged: any[] = [];

    if (ownIds.length > 0) {
      const { data: ownRows, error: ownErr } = await supabase
        .from('users')
        .select(selectFields)
        .in('id', ownIds);
      if (ownErr) {
        console.error('❌ V161.11: Batch profile query failed (own):', ownErr);
      } else if (ownRows) {
        merged.push(...ownRows);
      }
    }

    if (peerIds.length > 0) {
      const { data: peerRows, error: peerErr } = await rpcPeerUsersForViewer(supabase, peerIds, {
        includePrefs: true
      });
      if (peerErr) {
        console.error('❌ V161.11: Batch profile query failed (peers):', peerErr);
      } else if (peerRows) {
        merged.push(...peerRows.map((p: any) => ({ ...p, email: p.email ?? '' })));
      }
    }

    if (merged.length > 0) {
      merged.forEach((profile: any) => {
        results.set(profile.id, profile);
        
        // Cache individual profiles
        const cacheKey = `profile:${profile.id}:${options.includeProfiles}:${options.fields?.join(',') || ''}`;
        profileCache.set(cacheKey, {
          data: profile,
          timestamp: Date.now()
        });
      });
    }

    console.log(`✅ V161.11: Batch loaded ${results.size} profiles`);
    return results;
  } catch (error) {
    console.error('❌ V161.11: Batch profile loading failed:', error);
    return results;
  }
}

/**
 * V161.11: Internal function to fetch user profile data
 */
async function fetchUserProfileData(
  userId: string,
  options: ProfileRequestOptions
): Promise<UserProfileData | null> {
  try {
    const selectFields = buildSelectFields(options);
    const { data: authData } = await supabase.auth.getUser();
    const selfId = authData?.user?.id;

    if (selfId === userId) {
      const { data, error } = await supabase
        .from('users')
        .select(selectFields)
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error(`❌ V161.11: Profile query failed for ${userId}:`, error);
        return null;
      }

      if (data && data.date_of_birth && !data.dob) {
        data.dob = data.date_of_birth;
      }
      return data;
    }

    const { data: peerRows, error: peerErr } = await rpcPeerUsersForViewer(supabase, [userId], {
      includePrefs: true
    });
    if (peerErr) {
      console.error(`❌ V161.11: Profile query failed for ${userId}:`, peerErr);
      return null;
    }
    const data = peerRows?.[0] as any;
    if (!data) return null;

    if (data.date_of_birth && !data.dob) {
      data.dob = data.date_of_birth;
    }
    data.email = data.email ?? '';
    return data;
  } catch (error) {
    console.error(`❌ V161.11: Profile fetch failed for ${userId}:`, error);
    return null;
  }
}

/**
 * V161.11: Build optimized select fields based on options
 */
function buildSelectFields(options: ProfileRequestOptions): string {
  const baseFields = [
    'id', 'name', 'email', 'photo_urls', 'bio', 'date_of_birth', 'gender',
    'trust_score', 'is_verified', 'dating_preferences', 'account_status',
    'created_at', 'updated_at'
  ];

  let selectFields = options.fields?.length ? options.fields : baseFields;

  // Add profiles join if requested
  if (options.includeProfiles) {
    const profileFields = [
      'id',
      'relationship_status', 'dating_goal'
    ];
    
    selectFields.push(`profiles!left(${profileFields.join(',')})`);
  }

  return selectFields.join(',');
}

// =============================================================================
// CONVENIENCE FUNCTIONS
// =============================================================================

/**
 * V161.11: Get basic user info (name, photo, email)
 * Optimized for avatar/display purposes
 */
export async function getUserBasicInfo(userId: string): Promise<UserProfileData | null> {
  return getUserProfile(userId, {
    fields: ['id', 'name', 'email', 'photo_urls'],
    includeProfiles: false,
    useCache: true
  });
}

/**
 * V161.11: Get complete user profile with all data
 * For profile pages and detailed views
 */
export async function getUserCompleteProfile(userId: string): Promise<UserProfileData | null> {
  return getUserProfile(userId, {
    includeProfiles: true,
    useCache: true
  });
}

/**
 * V323: Just-in-time profile injection for real-time match events.
 * Pre-populates cache so UI (dialog, chat list) shows correct name/avatar immediately.
 */
export function injectUserProfile(
  userId: string,
  data: { id?: string; name?: string; photo_urls?: string[] }
): void {
  if (!userId) return;
  const profile = {
    id: userId,
    name: data.name ?? 'Someone',
    photo_urls: data.photo_urls ?? [],
    ...data
  };
  const cacheKey = `profile:${userId}:true:`; // includeProfiles:true, no extra fields
  profileCache.set(cacheKey, {
    data: profile,
    timestamp: Date.now()
  });
  // Also cache basic/display variant
  const basicKey = `profile:${userId}:false:id,name,email,photo_urls`;
  profileCache.set(basicKey, {
    data: profile,
    timestamp: Date.now()
  });
}

/**
 * V161.11: Clear profile cache for specific user
 */
export function clearUserProfileCache(userId: string): void {
  const keysToDelete = Array.from(profileCache.keys()).filter(key => 
    key.startsWith(`profile:${userId}:`)
  );
  
  keysToDelete.forEach(key => profileCache.delete(key));
  console.log(`🧹 V161.11: Cleared ${keysToDelete.length} cache entries for ${userId}`);
}

/**
 * V161.11: Get cache statistics for debugging
 */
export function getProfileCacheStats(): {
  cacheSize: number;
  pendingRequests: number;
  requestCounts: Map<string, number>;
} {
  return {
    cacheSize: profileCache.size,
    pendingRequests: pendingProfileRequests.size,
    requestCounts: new Map(profileRequestCounts)
  };
}
